import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Hourglass, RefreshCcw, ShieldCheck, LogIn } from "lucide-react";
import { relTime } from "@/components/blacklist/GraceCountdowns";

const discordSignIn = async () => {
  try {
    const { data } = await api.get("/auth/discord/login", { params: { next: "/blacklist-recheck" } });
    window.location.href = data.url;
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Discord sign-in is unavailable"); }
};

function SignInCard({ text }) {
  return (
    <div className="surface-card p-6 space-y-4" data-testid="recheck-signin">
      <p className="text-sm text-[var(--ff-text-dim)]">{text}</p>
      <button onClick={discordSignIn} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="recheck-signin-btn"><LogIn size={14} /> Sign in with Discord</button>
    </div>
  );
}

// Members warned about a blacklisted server confirm they left, clearing the countdown early.
export default function BlacklistRecheck() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const load = useCallback(() => api.get("/me/blacklist-status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { if (user?.id) load(); }, [user?.id, load]);

  const recheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/me/blacklist-recheck");
      setStatus((s) => ({ ...s, countdowns: data.countdowns, banned: data.banned }));
      setReconnect(data.needs_reconnect);
      if (data.cleared.length) toast.success(`Thanks! Cleared: ${data.cleared.join(", ")}`);
      else if (data.needs_reconnect) toast.error("We couldn't read your server list — reconnect Discord below.");
      else if (data.countdowns.length) toast.error("You're still in a blacklisted server.");
      else toast.success("You're all clear");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 space-y-8" data-testid="blacklist-recheck-page">
      <div>
        <div className="label-eyebrow text-amber-300 mb-3 flex items-center gap-2"><Hourglass size={12} /> ▮ BLACKLISTED SERVER CHECK</div>
        <h1 className="font-display text-4xl sm:text-5xl">Left the server?</h1>
        <p className="text-sm text-[var(--ff-text-dim)] mt-3">If our bot warned you about a blacklisted server, leave it in Discord, then re-check here to clear your countdown before the deadline.</p>
      </div>
      {loading ? null : !user ? (
        <SignInCard text="Sign in with the same Discord account that got the warning." />
      ) : !status ? null : !status.linked ? (
        <SignInCard text="Your account isn't linked to Discord yet — sign in with Discord to check." />
      ) : (
        <div className="space-y-4">
          {status.countdowns.length === 0 ? (
            <div className="surface-card p-6 flex items-center gap-3 text-emerald-400" data-testid="recheck-clear"><ShieldCheck size={20} /> You have no active warnings. You&apos;re all clear!</div>
          ) : (
            <ul className="space-y-2" data-testid="recheck-countdowns">
              {status.countdowns.map((c) => (
                <li key={c.id} className="surface-card p-4 flex flex-wrap items-center gap-3" data-testid={`recheck-countdown-${c.id}`}>
                  <span className="font-display flex-1">{c.guild_name}</span>
                  <span className="text-xs text-[var(--ff-text-dim)]">Leave by {new Date(c.deadline).toLocaleString()}</span>
                  <span className={`font-mono-ui text-xs ${new Date(c.deadline) > Date.now() ? "text-amber-300" : "text-[var(--ff-red)]"}`}>{relTime(c.deadline)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={recheck} disabled={checking} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="recheck-btn">
              <RefreshCcw size={14} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "I left — re-check me"}
            </button>
            {reconnect && <button onClick={discordSignIn} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="recheck-reconnect-btn"><LogIn size={14} /> Reconnect Discord</button>}
          </div>
          {status.banned && <p className="text-xs text-[var(--ff-red)]" data-testid="recheck-banned-note">Your account is currently banned. You can appeal on the <a href="/banned" className="underline">appeal page</a>.</p>}
        </div>
      )}
    </div>
  );
}
